// Salaires & Bonus — payroll screen (soft-island port of the
// design/salaires-bonus prototype). Monthly salary table + quarterly
// bonus arbitration + global 3-step workflow (Saisie → Validation →
// Envoi banque) + bank-file export.
//
// Deviations from the prototype (deliberate, see plan):
// - the proto's own topbar/entity pills/Tweaks panel are dropped —
//   the app shell + EntityBar own those; entity pills only FILTER
//   this view (the workflow + bank file stay global),
// - stepper labels are generic role names, not hardcoded people,
//   but the steps are no longer interchangeable: preparing the month
//   (payroll.edit) and releasing it (payroll.validate) are two keys,
//   so whoever saisit cannot be the one who unlocks the bank files,
// - any month is navigable (proto was clamped to T2-2026),
// - once a grille mensuelle is validated, the month's facts (absences,
//   maladie, congés, entrées…) sit above the table and the salaire du
//   mois is calculated from them; a « Grille paie » tab shows that
//   validated grille with the salaries next to the names.

import { useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import type { PayrollStage } from '@pointage/shared';
import {
  useMe,
  usePayrollMonth,
  useReopenPayroll,
  useSendBackPayroll,
  useSubmitPayroll,
  useValidatePayroll,
} from '../api/hooks.js';
import { useUiStore } from '../store/ui.js';
import { toast } from '../store/toast.js';
import { fmtDA } from '../lib/format.js';
import { monthLabel } from '../components/salaires/common.js';
import { SalairesTable } from '../components/salaires/SalairesTable.js';
import { QuarterView } from '../components/salaires/QuarterView.js';
import { BankExportModal } from '../components/salaires/BankExportModal.js';
import { MonthFactsPanel } from '../components/salaires/MonthFacts.js';
import { GrillePaieView } from '../components/salaires/GrillePaieView.js';
import '../styles/salaires-bonus.css';

const STEPS = [
  { who: 'Saisie', role: 'salaires & notes' },
  { who: 'Validation', role: 'seconde signature' },
  { who: 'Envoi banque', role: 'fichiers virements' },
];
const STAGE_IDX: Record<PayrollStage, number> = { SAISIE: 0, VALIDATION: 1, ENVOI: 2, ENVOYE: 3 };

export function SalairesPage() {
  const me = useMe();
  const themeDark = useUiStore((s) => s.theme) === 'dark';
  const entityId = useUiStore((s) => s.currentEntityId);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const now = new Date();
  const year = Number(searchParams.get('year')) || now.getFullYear();
  const month = Number(searchParams.get('month')) || now.getMonth() + 1;
  const viewParam = searchParams.get('view');
  const view = viewParam === 'trimestre' || viewParam === 'grille' ? viewParam : 'mois';

  const [exportOpen, setExportOpen] = useState(false);
  const [focusId, setFocusId] = useState<string | null>(null);

  const perms = me.data?.permissions ?? [];
  const canView = perms.includes('payroll.view');
  const canEditPerm = perms.includes('payroll.edit');
  const canValidate = perms.includes('payroll.validate');
  const canExport = perms.includes('payroll.export');

  const query = usePayrollMonth({ year, month, enabled: canView });
  const submit = useSubmitPayroll();
  const validate = useValidatePayroll();
  const sendBack = useSendBackPayroll();
  const reopen = useReopenPayroll();

  // ASSISTANT (no payroll.view) never sees this page.
  if (me.data && !canView) return <Navigate to="/dashboard" replace />;

  const setParams = (patch: Record<string, string | null>) => {
    const p = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(patch)) {
      if (v == null) p.delete(k);
      else p.set(k, v);
    }
    setSearchParams(p, { replace: true });
  };
  const setMonth = (y: number, m: number) => setParams({ year: String(y), month: String(m) });
  const prevMonth = () => (month === 1 ? setMonth(year - 1, 12) : setMonth(year, month - 1));
  const nextMonth = () => (month === 12 ? setMonth(year + 1, 1) : setMonth(year, month + 1));

  const data = query.data;
  const stage: PayrollStage = data?.stage ?? 'SAISIE';
  const sent = stage === 'ENVOYE';
  // Saisie: le préparateur écrit. Soumis: le mois appartient au
  // valideur, seul à pouvoir encore rectifier un montant. Envoyé:
  // plus personne (le serveur applique la même règle).
  const canEdit = !sent && (stage === 'SAISIE' ? canEditPerm : canValidate);
  const stageIdx = STAGE_IDX[stage];
  const q = data?.quarter ?? Math.ceil(month / 3);
  const isQEnd = data?.isQuarterEnd ?? month % 3 === 0;

  const all = data?.employees ?? [];
  const team = all.filter((e) => entityId === 'all' || e.entityId === entityId);
  // Entités dont la grille mensuelle n'est pas validée : l'arbitrage du
  // mois n'est pas fini, la paie ne peut pas partir en validation.
  const blockers = data?.gridBlockers ?? [];

  // Workflow counts are GLOBAL (the bank file covers all entities) —
  // shown even when a pill filters the table.
  const toValidateAll = all.filter((e) => e.status === 'SAISI').length;
  const toValidateVisible = team.filter((e) => e.status === 'SAISI').length;
  const undefBonus = team.filter((e) => e.finalBonus == null).length;
  const informed = team.filter((e) => e.informed).length;
  const totSal = team.reduce((a, e) => a + (sent ? e.paidSalary ?? e.salary : e.salary), 0);
  const totRef = team.reduce((a, e) => a + e.referenceSalary, 0);
  const totBon = team.reduce((a, e) => a + (isQEnd ? e.finalBonus ?? 0 : 0), 0);

  const goQuarter = (id: string) => {
    setParams({ view: 'trimestre' });
    setFocusId(id);
  };

  // Le serveur revérifie tout : le `force` d'ici n'est qu'un raccourci
  // d'écran, refusé si l'appelant n'a pas payroll.validate.
  const doSubmit = async (force = false) => {
    try {
      await submit.mutateAsync({ year, month, force });
      toast.success('Paie soumise pour validation.');
    } catch (err) {
      const e = err as Error & {
        body?: { error?: string; message?: string; count?: number; canForce?: boolean };
      };
      if (e.body?.error === 'grid_pending' && e.body.canForce && !force) {
        const ok = window.confirm(
          `${e.body.message}\n\nSoumettre quand même ? La dérogation est enregistrée à votre nom dans le journal d'activité.`,
        );
        if (ok) return doSubmit(true);
        return;
      }
      toast.danger(e.body?.message ?? 'Échec de la soumission.');
    }
  };

  const doValidate = async () => {
    try {
      await validate.mutateAsync({ year, month });
      toast.success('Paie validée — fichiers banque débloqués.');
    } catch (err) {
      const e = err as Error & { body?: { message?: string } };
      toast.danger(e.body?.message ?? 'Échec de la validation.');
    }
  };

  const doSendBack = async () => {
    const reason = window.prompt('Renvoyer en saisie — motif (facultatif) :', '');
    if (reason === null) return;
    try {
      await sendBack.mutateAsync({ year, month, reason: reason.trim() || undefined });
      toast.success('Mois renvoyé en saisie.');
    } catch (err) {
      const e = err as Error & { body?: { message?: string } };
      toast.danger(e.body?.message ?? 'Échec du renvoi en saisie.');
    }
  };
  const doReopen = async () => {
    try {
      await reopen.mutateAsync({ year, month });
      toast.success('Mois rouvert — export banque à refaire.');
    } catch (err) {
      const e = err as Error & { body?: { message?: string } };
      toast.danger(e.body?.message ?? 'Échec de la réouverture.');
    }
  };

  // CTA per stage + key. Saisie is the preparer's; validation is the
  // owner's; neither can stand in for the other.
  const cta = (() => {
    if (sent) return { lbl: '✓ Virements envoyés', cls: 'locked' as const };
    if (stage === 'SAISIE') {
      if (!canEditPerm) return { lbl: 'Saisie en cours', cls: 'locked' as const };
      if (toValidateAll > 0) {
        return {
          lbl: `${toValidateAll} ligne${toValidateAll > 1 ? 's' : ''} à confirmer` +
            (toValidateAll !== toValidateVisible ? ` (dont ${toValidateAll - toValidateVisible} hors filtre)` : ''),
          cls: 'locked' as const,
        };
      }
      if (blockers.length > 0 && !canValidate) {
        return { lbl: 'Arbitrage du mois incomplet', cls: 'locked' as const };
      }
      return { lbl: 'Soumettre pour validation →', cls: 'ready' as const, fn: () => doSubmit() };
    }
    if (stage === 'VALIDATION') {
      return canValidate
        ? { lbl: '✓ Valider la paie', cls: 'ready' as const, fn: doValidate }
        : { lbl: 'En attente de validation', cls: 'locked' as const };
    }
    // ENVOI
    return canExport
      ? { lbl: '⇪ Générer les fichiers banque', cls: 'ready' as const, fn: () => setExportOpen(true) }
      : { lbl: 'Prêt pour envoi banque', cls: 'locked' as const };
  })();

  const banner = (() => {
    if (!data) return null;
    if (sent) {
      return {
        cls: 'done', ic: '✓',
        txt: <span className="gm-attn-txt"><b>Virements de {monthLabel(month).toLowerCase()} envoyés{data.stamps.sent.by ? ` par ${data.stamps.sent.by}` : ''}.</b> Mois verrouillé — salaires{isQEnd ? ` + bonus T${q}` : ''} inclus.</span>,
        go: canExport ? { lbl: 'Rouvrir le mois', fn: doReopen } : undefined,
      };
    }
    if (stage === 'ENVOI') {
      const virements = all.filter((e) => e.paymentMethod === 'VIREMENT').length;
      return {
        cls: 'done', ic: '✓',
        txt: <span className="gm-attn-txt"><b>Paie validée{data.stamps.validated.by ? ` par ${data.stamps.validated.by}` : ''}.</b> {virements} virement{virements > 1 ? 's' : ''} prêt{virements > 1 ? 's' : ''} — générer le fichier au format banque et l'uploader.</span>,
        go: canExport ? { lbl: '⇪ Générer le fichier', fn: () => setExportOpen(true) } : undefined,
      };
    }
    if (stage === 'VALIDATION') {
      return {
        cls: 'todo', ic: '?',
        txt: (
          <span className="gm-attn-txt">
            <b>Saisie soumise{data.stamps.submitted.by ? ` par ${data.stamps.submitted.by}` : ''}.</b>{' '}
            {canValidate
              ? <>Les montants sont figés pour le préparateur — vous seul pouvez encore les corriger{isQEnd && undefBonus > 0 ? `, et ${undefBonus} bonus T${q} restent à arrêter` : ''}.</>
              : <>Les montants sont figés jusqu'à la validation. Le fichier banque se débloquera ensuite.</>}
          </span>
        ),
        go: canValidate ? { lbl: '↩ Renvoyer en saisie', fn: doSendBack } : undefined,
      };
    }
    if (stage === 'SAISIE' && blockers.length > 0) {
      return {
        cls: 'todo', ic: '!',
        txt: (
          <span className="gm-attn-txt">
            <b>Arbitrage du mois incomplet.</b> Grille mensuelle non validée : {blockers.map((b) => `${b.entityName} (${b.stageLabel})`).join(', ')}. La paie ne peut pas être soumise tant qu'une grille reste ouverte.
          </span>
        ),
        go: { lbl: 'Ouvrir la grille', fn: () => navigate(`/monthly?entity=${blockers[0]!.entityId}&year=${year}&month=${month}`) },
      };
    }
    if (stage === 'SAISIE' && isQEnd && canEditPerm) {
      return {
        cls: 'todo', ic: '★',
        txt: <span className="gm-attn-txt"><b>{monthLabel(month)} = fin du trimestre.</b> Vérifiez les notes des mois précédents dans l'onglet Bonus T{q} — le cumul des provisoires sert de proposition.</span>,
        go: { lbl: `Ouvrir Bonus T${q}`, fn: () => setParams({ view: 'trimestre' }) },
      };
    }
    return null;
  })();

  return (
    <div className={'sbapp' + (themeDark ? ' theme-dark' : '')}>
      <div className="sb-scrollpage">
        <div className="gm-wrap view-enter" key={view}>
          <div className="gm-eyebrow">{monthLabel(month)} {year} · Trimestre {q}</div>
          <h1 className="gm-title">Salaires &amp; bonus <em>de l'équipe</em></h1>
          <p className="gm-sub">Notes mensuelles → bonus trimestriel → salaire final → fichier banque. Saisie, validation, puis envoi des virements.</p>

          <div className="gm-ctrl">
            <div className="gm-monthnav">
              <button type="button" className="gm-mn-arrow" onClick={prevMonth}>‹</button>
              <span className="gm-mn-lbl">
                📅 {monthLabel(month).toLowerCase()} {year}{' '}
                <span className="gm-mn-q">T{q}{isQEnd ? ' · fin' : ''}</span>
              </span>
              <button type="button" className="gm-mn-arrow" onClick={nextMonth}>›</button>
            </div>
            <div className="gm-seg">
              <button type="button" className={view === 'mois' ? 'on2' : ''} onClick={() => setParams({ view: null })}>
                <span className="dot" />Salaires du mois
              </button>
              <button type="button" className={view === 'trimestre' ? 'on2' : ''} onClick={() => setParams({ view: 'trimestre' })}>
                <span className="dot" />Bonus T{q}
              </button>
              <button type="button" className={view === 'grille' ? 'on2' : ''} onClick={() => setParams({ view: 'grille' })}>
                <span className="dot" />Grille paie
              </button>
            </div>
            <div className="gm-stepper">
              {STEPS.map((s, i) => (
                <span key={i} style={{ display: 'contents' }}>
                  {i > 0 && <span className={'gm-step-line' + (i <= stageIdx ? ' done' : '')} />}
                  <div className={'gm-step' + (i < stageIdx ? ' done' : i === stageIdx ? ' current' : '')}>
                    <span className="gm-step-dot">{i < stageIdx ? '✓' : i + 1}</span>
                    <span className="gm-step-meta">
                      <span className="gm-step-who">{s.who}</span>
                      <span className="gm-step-role">{s.role}</span>
                    </span>
                  </div>
                </span>
              ))}
            </div>
            <div className="gm-ctrlspace" />
            <button type="button" className="gm-pdf" onClick={() => setExportOpen(true)}>⇪ Aperçu fichiers banque</button>
            {/* Un CTA « locked » est réellement désactivé, pas seulement
                grisé : sinon il reste focusable et cliquable dans le
                vide, et un lecteur d'écran l'annonce comme actionnable. */}
            <button
              type="button"
              className={'gm-submit ' + cta.cls}
              onClick={'fn' in cta ? cta.fn : undefined}
              disabled={!('fn' in cta) || submit.isPending || validate.isPending}
            >
              {cta.lbl}
            </button>
          </div>

          <div className="sb-kpis">
            <div className="sb-kpi">
              <span className="sb-kpi-v">{fmtDA(totSal)} <i>DA</i></span>
              <span className="sb-kpi-l">Masse salariale · {monthLabel(month).toLowerCase()}</span>
              {totSal !== totRef && (
                <span className="sb-kpi-sub">
                  habituelle {fmtDA(totRef)} · écart <b className="gm-mono">{totSal > totRef ? '+' : '−'}{fmtDA(Math.abs(totSal - totRef))}</b>
                </span>
              )}
            </div>
            <div className="sb-kpi">
              <span className="sb-kpi-v accent">+{fmtDA(totBon)} <i>DA</i></span>
              <span className="sb-kpi-l">Bonus T{q} arrêtés ({team.length - undefBonus}/{team.length})</span>
            </div>
            <div className="sb-kpi">
              <span className="sb-kpi-v">{team.length - toValidateVisible}<i>/{team.length}</i></span>
              <span className="sb-kpi-l">Lignes validées</span>
            </div>
            <div className="sb-kpi">
              <span className="sb-kpi-v">{informed}<i>/{team.length}</i></span>
              <span className="sb-kpi-l">Employés informés du bonus</span>
            </div>
          </div>

          {banner && (
            <div className={'gm-attn ' + banner.cls}>
              <span className="gm-attn-ic">{banner.ic}</span>
              {banner.txt}
              {banner.go && <button type="button" className="gm-attn-go" onClick={banner.go.fn}>{banner.go.lbl}</button>}
            </div>
          )}

          {query.isLoading && <div className="sb-placeholder">Chargement de la paie…</div>}
          {query.isError && <div className="sb-placeholder">Impossible de charger la paie — réessayez.</div>}
          {data && team.length === 0 && (
            <div className="sb-placeholder">Aucun employé pour ce filtre — choisissez « Toutes » dans la barre du haut.</div>
          )}
          {data && team.length > 0 && view === 'mois' && (
            <MonthFactsPanel team={team} monthName={monthLabel(month).toLowerCase()} />
          )}
          {data && team.length > 0 && (
            view === 'grille' ? (
              <GrillePaieView year={year} month={month} team={team} sent={sent} />
            ) : view === 'mois' ? (
              <SalairesTable
                data={data}
                team={team}
                canEdit={canEdit}
                canEditNotes={canEditPerm}
                onOpenQuarter={goQuarter}
              />
            ) : (
              <QuarterView
                data={data}
                team={team}
                // Bonuses follow the QUARTER-END month's stage, not the
                // viewed one (server-enforced; this mirrors it for the
                // common case of working the quarter-end month itself).
                canEdit={isQEnd ? canEdit : canEditPerm}
                focusId={focusId}
                onClearFocus={() => setFocusId(null)}
              />
            )
          )}
        </div>
      </div>
      {exportOpen && data && (
        <BankExportModal data={data} canExport={canExport} onClose={() => setExportOpen(false)} />
      )}
    </div>
  );
}
